import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Spinner } from '@nextui-org/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { ErrorLine, TabIntro, useAdminLoad } from '../aiAccess/shared';
import type { Batch, EnrichmentClient, ImportResponse } from './api';
import { FilePicker } from './FilePicker';
import { localTime } from './ProductView';

export const IMPORT_RESULTS_SHOWN = 200;

const COUNT_LABELS: Record<string, string> = {
  stored: 'stored',
  found: 'found',
  not_found: 'not found',
  failed: 'failed',
  skipped: 'skipped',
};

const KINDS: Record<string, string> = {
  bulk_lookup: 'Bulk lookup',
  file_import: 'File import',
};

const STATES: Record<string, { label: string; color: 'primary' | 'success' | 'warning' | 'danger' | 'default' }> = {
  running: { label: 'Running', color: 'primary' },
  completed: { label: 'Completed', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'warning' },
  failed: { label: 'Failed', color: 'danger' },
};

/** "12 found · 3 not found" for the counts that are not zero, in a fixed order. */
function countsText(counts: Record<string, number> | null | undefined, t: (key: string) => string): string {
  const order = [...Object.keys(COUNT_LABELS), ...Object.keys(counts ?? {}).filter((key) => !(key in COUNT_LABELS))];
  return order
    .filter((key) => (counts?.[key] ?? 0) > 0)
    .map((key) => `${counts?.[key]} ${t(COUNT_LABELS[key] ?? key)}`)
    .join(' · ');
}

/**
 * Import answers: the warranty agent's own answers, enriched outside the service (Dell, run
 * locally), stored like lookups in one batch. Below it, the tenant's recent uploads.
 */
export function ImportSection({ client, version, onImported }: {
  readonly client: EnrichmentClient | null;
  readonly version: number;
  readonly onImported: () => void;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResponse | null>(null);

  const importFile = async () => {
    if (!client || !file) return;
    setImporting(true);
    setError(null);
    setResult(null);
    try {
      setResult(await client.importAnswers(file));
      onImported();
    } catch (e) {
      setError((e as Error).message || t('Something went wrong.'));
    } finally {
      setImporting(false);
    }
  };

  const results = result?.results ?? [];

  return (
    <div className="flex flex-col gap-6">
      <section aria-label={t('Import answers')} className="flex flex-col gap-3">
        <TabIntro title={t('Import answers')}>
          <span className="text-sm text-default-500">
            {t('Answers the warranty agent produced elsewhere (Dell serials enriched locally): a .json, .jsonl or .zip file. Each is stored like a lookup’s answer.')}
          </span>
        </TabIntro>
        <div className="flex flex-wrap items-center gap-2">
          <FilePicker
            label={t('Answers file')}
            buttonLabel={t('Choose a file')}
            accept=".json,.jsonl,.zip"
            fileName={file?.name ?? null}
            isDisabled={!client || importing}
            onChoose={(next) => {
              setFile(next);
              setResult(null);
              setError(null);
            }}
          />
          <CtyButton color="primary" size="sm" onPress={() => void importFile()} isDisabled={!client || !file || importing} isLoading={importing}>
            {t('Import')}
          </CtyButton>
        </div>
        <ErrorLine message={error} />
        {result && (
          <div className="flex flex-col gap-2">
            <span role="status" className="text-sm">
              {t('Imported {{file}}: {{counts}}.', { file: file?.name ?? '', counts: countsText(result.counts, t) || t('nothing stored') })}
            </span>
            {results.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" aria-label={t('Imported answers')}>
                  <thead className="text-xs text-default-500">
                    <tr>
                      <th className="py-1 pr-4 font-medium">#</th>
                      <th className="py-1 pr-4 font-medium">{t('Serial')}</th>
                      <th className="py-1 pr-4 font-medium">{t('Result')}</th>
                      <th className="py-1 pr-4 font-medium">{t('Coverages')}</th>
                      <th className="py-1 font-medium">{t('Reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.slice(0, IMPORT_RESULTS_SHOWN).map((item) => (
                      <tr key={item.index} className="border-t border-default-100">
                        <td className="py-1 pr-4 text-xs">{item.index + 1}</td>
                        <td className="py-1 pr-4 font-mono text-xs">{item.serial_number || '—'}</td>
                        <td className="py-1 pr-4 text-xs">
                          {item.result}
                          {item.found ? <span className="text-default-500"> ({t('unit found')})</span> : null}
                        </td>
                        <td className="py-1 pr-4 text-xs">{item.coverages ?? '—'}</td>
                        <td className="py-1 text-xs text-default-500">{item.reason ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {results.length > IMPORT_RESULTS_SHOWN && (
                  <p className="text-xs text-default-500">
                    {t('Showing {{shown}} of {{total}} answers.', { shown: IMPORT_RESULTS_SHOWN, total: results.length })}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </section>
      <RecentUploads client={client} version={version} />
    </div>
  );
}

/** The tenant's recent bulk lookups and imports, newest first. */
function RecentUploads({ client, version }: { readonly client: EnrichmentClient | null; readonly version: number }) {
  const { t } = useTranslation();
  const { data, loading, error, reload } = useAdminLoad<Batch[], EnrichmentClient>(client, (c, signal) => c.batches(undefined, signal));
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const firstVersion = useRef(version);
  useEffect(() => {
    if (version !== firstVersion.current) void reloadRef.current();
  }, [version]);
  const batches = Array.isArray(data) ? data : [];

  return (
    <section aria-label={t('Recent uploads')} className="flex flex-col gap-3">
      <TabIntro
        title={t('Recent uploads')}
        actions={(
          <CtyButton variant="flat" size="sm" onPress={() => void reload()} isDisabled={loading || !client}>
            {t('Refresh')}
          </CtyButton>
        )}
      >
        <span className="text-sm text-default-500">{t('Bulk lookups and imports, newest first.')}</span>
      </TabIntro>
      {loading && <Spinner size="sm" className="self-start" />}
      <ErrorLine message={error} />
      {data && batches.length === 0 && <p className="text-sm text-default-500">{t('Nothing has been uploaded yet.')}</p>}
      {batches.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" aria-label={t('Recent uploads')}>
            <thead className="text-xs text-default-500">
              <tr>
                <th className="py-1 pr-4 font-medium">{t('File')}</th>
                <th className="py-1 pr-4 font-medium">{t('Kind')}</th>
                <th className="py-1 pr-4 font-medium">{t('State')}</th>
                <th className="py-1 pr-4 font-medium">{t('Rows')}</th>
                <th className="py-1 pr-4 font-medium">{t('Counts')}</th>
                <th className="py-1 pr-4 font-medium">{t('By')}</th>
                <th className="py-1 font-medium">{t('When')}</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch, index) => {
                const state = STATES[batch.state ?? ''] ?? { label: batch.state || '—', color: 'default' as const };
                return (
                  <tr key={batch.id ?? index} className="border-t border-default-100 align-top">
                    <td className="py-1 pr-4 text-xs">{batch.file_name || '—'}</td>
                    <td className="py-1 pr-4 text-xs">{t(KINDS[batch.kind ?? ''] ?? batch.kind ?? '—')}</td>
                    <td className="py-1 pr-4">
                      <Chip size="sm" variant="flat" color={state.color}>{t(state.label)}</Chip>
                    </td>
                    <td className="py-1 pr-4 text-xs">{batch.row_count ?? '—'}</td>
                    <td className="py-1 pr-4 text-xs">{countsText(batch.counts, t) || '—'}</td>
                    <td className="py-1 pr-4 text-xs">{batch.submitted_by_name || '—'}</td>
                    <td className="whitespace-nowrap py-1 text-xs" title={batch.finished_at ? t('Finished {{when}}', { when: localTime(batch.finished_at) }) : undefined}>
                      {localTime(batch.started_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
